import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(formData.username, formData.password);
      console.log('Login successful:', result);
      // Delay navigation to ensure state updates are complete
      setTimeout(() => {
        navigate('/');
      }, 300);
    } catch (error) {
      console.log('Login failed. Error object:', error);
      console.log('Error response:', error.response);
      const errorMessage = error.response?.data?.detail || t('toast.loginFailed');
      const translatedError = t(`auth.login.errors.${errorMessage}`) || errorMessage;
      console.log('Final error message to display:', translatedError);
      toast.error(translatedError, { autoClose: 2000 });
      // Clear the form fields after error
      setFormData({
        username: '',
        password: '',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={t('auth.login.seoTitle') || 'Login - Customer Account Access'}
        description={t('auth.login.seoDescription') || 'Login to your Meller Racing account to manage orders, track shipments, and access exclusive deals on motorcycle performance parts.'}
        keywords="login, customer account, sign in, member area"
        noindex={true}
      />

      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="text-center text-3xl font-display font-bold text-gray-900">
              {t('auth.login.title')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('auth.login.subtitlePrefix')} {' '}
              <Link to="/register" className="font-medium text-racing-red hover:underline">
                {t('auth.login.subtitleLink')}
              </Link>
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex justify-between items-start">
                <p className="text-sm text-red-700 flex-1">{error}</p>
                <button
                  type="button"
                  onClick={() => setError('')}
                  className="text-red-400 hover:text-red-600 ml-2 flex-shrink-0 font-bold"
                  aria-label="Close error message"
                >
                  ×
                </button>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  {t('auth.login.username')}
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="input-field mt-1"
                  placeholder={t('auth.login.usernamePlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('auth.login.password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input-field mt-1"
                  placeholder={t('auth.login.passwordPlaceholder')}
                />
                <div className="mt-2 text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-racing-red hover:underline"
                  >
                    {t('auth.login.forgot')}
                  </Link>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? (t('auth.login.submitting') || 'Logging in...') : t('auth.login.submit')}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default Login;
